/**
 * Color utilities for picking text color against arbitrary backgrounds.
 *
 * Used to keep button labels legible when the background is a team's primary
 * color — light teams (Cleveland Rocks, etc.) wash out white text and need
 * dark text instead.
 */

/**
 * Pick a legible text color (#000 or #fff) for a given background hex color.
 * Uses the standard WCAG relative-luminance formula.
 *
 * @param hex Background color, e.g. "#E91E8C" or "E91E8C"
 * @returns "#000000" for light backgrounds, "#ffffff" for dark backgrounds
 */
export function getContrastTextColor(hex: string): string {
  const h = (hex || '').replace('#', '')
  if (h.length !== 6) return '#ffffff'
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  // sRGB luminance approximation (good enough for picking text color)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#000000' : '#ffffff'
}

// ── Team color-clash handling ────────────────────────────────────────────────
// Two teams with similar primary colors are hard to tell apart on a WP meter /
// scoreboard / field. We flag a clash two ways:
//   1. A perceptually-weighted ("redmean") distance that weights green ~4x (the
//      eye's most sensitive channel) — catches near-identical colors of any hue
//      (two pinks like Melons/Broads, two greens like Rocks/Pinecones).
//   2. A hue-family check — adjacent hues read as "similar" even when their
//      redmean distance is larger (blue vs purple, yellow vs lime), so we also
//      clash colors whose hues sit within HUE_CLASH_DEGREES, gated on both being
//      saturated enough that hue is meaningful and not wildly different in
//      lightness (a light tint and a dark shade of one hue stay distinct).
const COLOR_CLASH_THRESHOLD = 155
const HUE_CLASH_DEGREES = 60          // adjacent hues within this clash (blue/purple ≈ 60°)
const HUE_CLASH_MIN_SAT = 0.25        // below this a color is too gray for hue to matter
const HUE_CLASH_MAX_LIGHT_DIFF = 0.35 // a light vs dark shade of one hue stays distinguishable

function hexToRgb(hex?: string | null): [number, number, number] | null {
  if (!hex) return null
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return [0, 0, l]
  const s = d / (1 - Math.abs(2 * l - 1))
  let h: number
  if (max === r) h = ((g - b) / d) % 6
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  h *= 60
  if (h < 0) h += 360
  return [h, s, l]
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let rgb: [number, number, number]
  if (h < 60) rgb = [c, x, 0]
  else if (h < 120) rgb = [x, c, 0]
  else if (h < 180) rgb = [0, c, x]
  else if (h < 240) rgb = [0, x, c]
  else if (h < 300) rgb = [x, 0, c]
  else rgb = [c, 0, x]
  return rgb.map(v => Math.round((v + m) * 255)) as [number, number, number]
}

const toHex = (rgb: [number, number, number]) =>
  '#' + rgb.map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')

/** WCAG relative luminance. */
function luminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

export function contrastRatio(a: string, b: string): number {
  const ra = hexToRgb(a), rb = hexToRgb(b)
  if (!ra || !rb) return 1
  const la = luminance(ra), lb = luminance(rb)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Make a team color legible AS TEXT on a dark background.
 *
 * Team colors are DATA, and a good few of them are navy, maroon or forest
 * green — perfectly good as a field or a border, but as small text on the
 * near-black page background they sink into it and the label disappears.
 * `getContrastTextColor` solves the opposite problem (what ink goes ON this
 * color); this one keeps the color's identity while lifting it far enough
 * off the background to read.
 *
 * Lightens in HSL, which holds the hue, so a navy team stays recognizably
 * navy — just a lighter navy. Falls back to the body gray only if even a
 * near-white version of the hue can't clear the bar (a pure-black brand).
 */
export function readableOnDark(
  hex?: string | null,
  background = '#0b1220',
  minRatio = 4.5,
): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return '#cbd5e1'
  // eslint-disable-next-line prefer-const
  let [h, s, l] = rgbToHsl(...rgb)
  // A very desaturated dark color just becomes gray when lightened, which is
  // legible but loses the point — give it a little saturation to keep an
  // identity.
  if (s < 0.15) s = Math.min(0.35, s + 0.2)
  for (let i = 0; i < 20; i++) {
    const candidate = toHex(hslToRgb(h, s, l))
    if (contrastRatio(candidate, background) >= minRatio) return candidate
    l += 0.04
    if (l > 0.97) break
  }
  return '#cbd5e1'
}

/** Redmean weighted euclidean distance. https://www.compuphase.com/cmetric.htm */
export function colorDistance(a?: string | null, b?: string | null): number {
  const ra = hexToRgb(a), rb = hexToRgb(b)
  if (!ra || !rb) return 0
  const dr = ra[0] - rb[0], dg = ra[1] - rb[1], db = ra[2] - rb[2]
  const rmean = (ra[0] + rb[0]) / 2
  return Math.sqrt(
    (2 + rmean / 256) * dr * dr +
    4 * dg * dg +
    (2 + (255 - rmean) / 256) * db * db,
  )
}

export function colorsTooClose(a?: string | null, b?: string | null): boolean {
  const ra = hexToRgb(a), rb = hexToRgb(b)
  if (!ra || !rb) return false
  const dist = colorDistance(a, b)
  if (dist < COLOR_CLASH_THRESHOLD) return true
  // 2) Same hue-family clash (blue/purple, yellow/lime): close hue, both
  // saturated, similar lightness.
  const [ha, sa, la] = rgbToHsl(ra[0], ra[1], ra[2])
  const [hb, sb, lb] = rgbToHsl(rb[0], rb[1], rb[2])
  let hueDiff = Math.abs(ha - hb)
  if (hueDiff > 180) hueDiff = 360 - hueDiff
  return (
    hueDiff <= HUE_CLASH_DEGREES &&
    Math.min(sa, sb) >= HUE_CLASH_MIN_SAT &&
    Math.abs(la - lb) <= HUE_CLASH_MAX_LIGHT_DIFF
  )
}

/**
 * Away team's effective display color. When its primary is basically the same
 * as the home primary, fall back to its secondary (only if that actually
 * separates from home) so the two stay distinguishable. Home is the reference.
 */
export function effectiveAwayColor(
  homeColor?: string | null,
  awayColor?: string | null,
  awaySecondary?: string | null,
  awayTertiary?: string | null,
): string {
  const away = awayColor ?? '#888'
  if (!colorsTooClose(homeColor, away)) return away

  // ⚠️ THE FALLBACK USED TO BE THE PRIMARY -- THE ONE OPTION ALREADY PROVED TO CLASH. This
  // read `secondary if it is clean, else the primary`, so a club whose secondary ALSO trips
  // `colorsTooClose` got back the colour we had just rejected. Reported on Minnesota Pops at
  // Kansas City Slippers, where both end zones of the drive line came out red.
  //
  // ⚠️ AND THE SECOND CLASH WAS ITSELF WRONG. Pops' secondary is a cream, **406 redmean units**
  // from the Slippers' crimson -- not remotely confusable -- but `colorsTooClose`'s hue-family
  // rule flagged it anyway: hueDiff 50.4 within 60, and a lightness difference of exactly
  // 0.35, right on its boundary. That rule exists for pairs that are close in DISTANCE too
  // (blue vs purple); it is not fixed here because other callers depend on its thresholds.
  //
  // So: once the primary is out, take whichever alternative sits FURTHEST from the home
  // colour rather than demanding a clean one. That is the right answer whether or not any
  // candidate passes, and it degrades to the primary only when there is nothing else.
  const candidates = [awaySecondary, awayTertiary].filter(Boolean) as string[]
  if (!candidates.length) return away
  let best = away, bestDist = colorDistance(homeColor, away)
  for (const c of candidates) {
    const d = colorDistance(homeColor, c)
    if (d > bestDist) { best = c; bestDist = d }
  }
  return best
}

// ── Team colors used as TEXT ─────────────────────────────────────────────────
// Bar fills use a team's raw primary. Text cannot: a good few config primaries are
// dark saturated magenta/violet/blue that sit under 4.5:1 on the redesigned card
// surfaces.
//
// `lightenColor` is the long-standing GameCard helper, hoisted here so there is one
// copy instead of the two it had inside that file. It is NOT sufficient on its own —
// its 0.45 luminance target still leaves 10 of the 32 teams short, and Philadelphia
// measures 4.41:1 on the pinned card. `readableTeamColor` is what components should
// actually call.

/** Lift a dark color toward 0.45 luminance. Returns the input if already light. */
export function lightenColor(hex: string): string {
  const h = (hex || '').replace('#', '')
  if (h.length !== 6) return hex || '#cbd5e1'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  if (luminance > 0.45) return `#${h}`
  const boost = 0.45 / Math.max(luminance, 0.05)
  const clamp = (v: number) => Math.min(255, Math.round(v * boost))
  return `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`
}

/**
 * The card surfaces a team-colored label is drawn on in the redesigned pages.
 * `#17222f` (the own-team card) is the LIGHTER of the two, so it is the binding
 * constraint for light text — clearing it clears `#131e2f` as well.
 */
export const TEAM_TEXT_BACKGROUND = '#17222f'

/**
 * A team's color, made safe to use as TEXT on the redesigned card surfaces.
 *
 * Delegates to `readableOnDark`, which lifts in HSL and therefore keeps the hue — a
 * navy team stays recognizably navy rather than drifting toward white, which is what
 * a blend-toward-#f8fafc correction does to it.
 *
 * Apply it to BOTH sides of a paired figure (the two halves of a win-probability
 * gauge, say). Correcting only the favoured side leaves the same number pair wildly
 * mismatched in legibility.
 */
export function readableTeamColor(
  hex?: string | null,
  background: string = TEAM_TEXT_BACKGROUND,
  minRatio = 4.6,
): string {
  return readableOnDark(hex, background, minRatio)
}
